import React from "react";
import { ShieldAlert, CalendarDays, Percent, Network, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface LeakCard {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  ariaLabel: string;
}
const leakCards: LeakCard[] = [{
  id: "01",
  title: "The Parity Trap",
  description: "OTAs like Agoda and Booking.com often apply localized markdowns or cashbacks, selling your rooms cheaper than your front desk. I audit listings daily to shut down unauthorized discounts and enforce strict channel parity.",
  icon: ShieldAlert,
  ariaLabel: "The Parity Trap icon"
}, {
  id: "02",
  title: "Flat Weekend Rates",
  description: "Leaving Friday and Saturday rates unchanged until 48 hours before check-in costs you margin. I track real-time booking pickup pace to lift ADR during demand surges and set MLOS rules on high-compression dates.",
  icon: CalendarDays,
  ariaLabel: "Flat Weekend Rates icon"
}, {
  id: "03",
  title: "High Commission Dependency",
  description: "Paying 18% to 22% commission on guests who would have booked direct erodes your GOPPAR. I set rate fences and package incentives so your official website remains your most profitable channel.",
  icon: Percent,
  ariaLabel: "High Commission Dependency icon"
}, {
  id: "04",
  title: "Misconfigured Channel Managers",
  description: "Mapping errors between your PMS and channel manager trigger accidental overbookings on peak dates and hidden inventory stalls during shoulder periods. I audit your complete tech stack to keep inventory in sync.",
  icon: Network,
  ariaLabel: "Misconfigured Channel Managers icon"
}];
export default function WhereHotelsLeakRevenue() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="where-hotels-leak-revenue" id="where-hotels-leak-revenue" className="relative overflow-hidden border-t border-border bg-background py-20 text-foreground md:py-28 lg:py-32">
      {/* Subtle ambient gold glow */}
      <div className="pointer-events-none absolute top-1/4 left-1/2 hidden h-[360px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-secondary/5 blur-[120px] sm:block" aria-hidden="true" />

      <div className="relative mx-auto w-full max-w-7xl px-6 md:px-8">
        {/* Header Block */}
        <div ref={header.ref} className="mx-auto max-w-3xl text-center">
          <div className={`transition-all duration-700 ease-out ${header.isVisible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"}`}>
            <p className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-secondary md:text-sm">
              Revenue Leakage
            </p>
            <h2 className="text-3xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Where Independent Hotels Actually Leak Revenue Every Day
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Hotels lose 12% to 20% of top-line revenue to hidden OTA discounting and static rates. Here are the 4 daily leaks I fix first:
            </p>
          </div>
        </div>

        {/* 4-Card Modular Grid (1 col mobile, 2x2 desktop) */}
        <div className="mt-14 grid grid-cols-1 gap-6 sm:mt-16 md:grid-cols-2 lg:gap-8">
          {leakCards.map((card, index) => <LeakCardItem key={card.id} card={card} index={index} />)}
        </div>

        {/* Closing CTA Banner */}
        <div ref={footer.ref} className="mt-14 text-center sm:mt-16">
          <div className={`mx-auto flex max-w-2xl flex-col items-center justify-center rounded-2xl border border-border bg-card p-8 shadow-sm transition-all duration-700 ease-out sm:p-10 ${footer.isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
            <p className="text-base font-medium text-foreground sm:text-lg">
              Not sure which leak is draining your margin? A 14-day audit identifies every issue on your extranets.
            </p>
            <div className="mt-6">
              <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90 font-semibold shadow-sm">
                <Link to="/ContactPage#start-your-revenue-journey">
                  <span>Request a Free Revenue Audit</span>
                  <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
interface LeakCardItemProps {
  card: LeakCard;
  index: number;
}
function LeakCardItem({
  card,
  index
}: LeakCardItemProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const delay = index % 2 * 120;
  const IconComponent = card.icon;
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${delay}ms`
  }} className={`group relative flex flex-col justify-between rounded-xl border border-border bg-card p-7 transition-all duration-300 hover:border-secondary/70 hover:bg-secondary/5 hover:shadow-[0_0_30px_-5px_hsla(43,46%,55%,0.18)] lg:p-8 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
      <div>
        {/* Card Header: Icon + Number Index */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex size-12 items-center justify-center rounded-xl border border-secondary/20 bg-secondary/10 text-secondary transition-colors duration-300 group-hover:border-secondary group-hover:bg-secondary group-hover:text-secondary-foreground">
            <IconComponent className="size-5" strokeWidth={1.75} />
          </div>
          <span className="font-mono text-xs font-medium tracking-wider text-muted-foreground transition-colors group-hover:text-secondary/80">
            {card.id}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold leading-snug tracking-tight text-foreground transition-colors duration-200 group-hover:text-secondary">
          {card.title}
        </h3>

        {/* Description */}
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
          {card.description}
        </p>
      </div>

      {/* Decorative divider / accent */}
      <div className="mt-6 h-[1px] w-full bg-border transition-colors duration-300 group-hover:bg-secondary/40" aria-hidden="true" />
    </div>;
}